import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";

import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { OrganizationId } from "@bitwarden/common/types/guid";
import { BannerComponent, ButtonModule, ToastService } from "@bitwarden/components";
import { I18nPipe } from "@bitwarden/ui-common";

import {
  PendingOrganizationInvitation,
  VaultOrganizationUserNotificationsService,
} from "../../services/vault-organization-user-notifications.service";

@Component({
  selector: "vault-organization-user-notifications",
  templateUrl: "./vault-organization-user-notifications.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BannerComponent, ButtonModule, I18nPipe],
  providers: [VaultOrganizationUserNotificationsService],
})
export class VaultOrganizationUserNotificationsComponent {
  private readonly vaultOrganizationUserNotificationsService = inject(
    VaultOrganizationUserNotificationsService,
  );
  private readonly router = inject(Router);
  private readonly i18nService = inject(I18nService);
  private readonly toastService = inject(ToastService);

  protected readonly notificationData = toSignal(
    this.vaultOrganizationUserNotificationsService.notificationData$,
  );

  protected readonly showBanner = toSignal(
    this.vaultOrganizationUserNotificationsService.showNotificationBanner$,
    { initialValue: false },
  );

  protected readonly pendingInvitations = toSignal(
    this.vaultOrganizationUserNotificationsService.pendingInvitations$,
    { initialValue: [] },
  );
  protected readonly acceptingInvitationId = signal<string | null>(null);

  protected async close() {
    await this.vaultOrganizationUserNotificationsService.saveDismissalToState();
  }

  protected async actionButtonClick(organizationId: OrganizationId) {
    await this.vaultOrganizationUserNotificationsService.recordActionButtonClick(organizationId);
  }

  protected async acceptInvitation(invitation: PendingOrganizationInvitation) {
    this.acceptingInvitationId.set(invitation.organizationUserId);
    try {
      const accepted = await this.vaultOrganizationUserNotificationsService.acceptPendingInvitation(
        invitation,
        this.router.url,
      );
      if (accepted) {
        this.toastService.showToast({
          message: this.i18nService.t("invitationAcceptedDesc"),
          variant: "success",
          timeout: 10000,
        });
      }
    } catch {
      this.toastService.showToast({
        message: this.i18nService.t("inviteAcceptFailed"),
        variant: "error",
        timeout: 10000,
      });
    } finally {
      this.acceptingInvitationId.set(null);
    }
  }
}
